import { Injectable } from '@angular/core';
import { ServiceItemType } from '@supoui/shared/model/service-item-type';
import { PurposeOfTravelItemType } from '@supoui/shared/model/purpose-of-travel-item-type';
import { CauseItemType } from '@supoui/shared/model/cause-item-type';
import { PhoneNumberTypeEnum } from '@supoui/shared/model/enumerations/phone-number-type.enum';
import moment from 'moment';
import { IApplicationCustomerProfile } from '@supoui/shared/model/application-customer-profile';

@Injectable({
  providedIn: 'root'
})
export class TaskWebPropertiesService {
  constructor() {}

  isShowProperty(obj: any, key: string, showEmptyProperties: boolean = false): boolean {
    return (
      showEmptyProperties ||
      this.isArrayProperty(obj, key) ||
      this.isTypeProperty(obj, key) ||
      this.isNestedProperty(obj, key) ||
      this.isBooleanProperty(obj, key) ||
      (this.isStringProperty(obj, key) &&
        // @ts-ignore
        (!!this.getPropertyValue(obj, key, null, null, null, null) || 0 === this.getPropertyValue(obj, key, null, null, null, null)))
    );
  }

  getPropertyKeys(obj: any, ignored: string[] = []): string[] {
    return obj ? Object.keys(obj).filter(key => !ignored || !ignored.includes(key)) : [];
  }

  isBooleanProperty(obj: any, key: string): boolean {
    const objVal = obj && obj[key];

    return objVal !== undefined && objVal !== null && typeof objVal === 'boolean';
  }

  isStringProperty(obj: any, key: string): boolean {
    const objVal = obj && obj[key];

    return objVal === undefined || objVal === null || typeof objVal === 'string' || typeof objVal === 'number';
  }

  isArrayProperty(obj: any, key: string): boolean {
    const objVal = obj && obj[key];

    return objVal && objVal['type'] === 'array';
  }

  isTypeProperty(obj: any, key: string): boolean {
    const objVal = obj && obj[key];

    return this.isKnownObjectType(objVal);
  }

  isNestedProperty(obj: any, key: string): boolean {
    return (
      !this.isStringProperty(obj, key) &&
      !this.isBooleanProperty(obj, key) &&
      !this.isArrayProperty(obj, key) &&
      !this.isTypeProperty(obj, key)
    );
  }

  getPropertyValue(
    obj: any,
    key: string,
    serviceItemList: ServiceItemType[],
    purposeOfTravelItemList: PurposeOfTravelItemType[],
    causes: Map<string, CauseItemType[]>,
    customerProfiles: IApplicationCustomerProfile[]
  ): any {
    const objVal = obj && obj[key];
    if (this.isBooleanProperty(obj, key)) {
      return objVal;
    }

    if (this.isStringProperty(obj, key)) {
      return objVal;
    }
    if (!objVal) {
      return '';
    }

    if (this.isKnownObjectType(objVal)) {
      return this.getObjectValueFromType(objVal, serviceItemList, purposeOfTravelItemList, causes, customerProfiles);
    }

    return '';
  }

  getArrayProperties(obj: any, key: string): any {
    const objVal = obj && obj[key];
    if (!objVal) {
      return [];
    }
    const type = objVal['type'];
    switch (type) {
      case 'array':
        const nestedObj = {};
        const arr = objVal[key];
        if (arr && Array.isArray(arr)) {
          arr.forEach((item, idx) => {
            if (item.name) {
              nestedObj[item.name] = item;
            } else if (item.type) {
              nestedObj[item.type] = item;
            } else {
              nestedObj[key + idx] = item;
            }
          });
        }
        return [nestedObj];
    }

    return [];
  }

  getObjectValueFromType(
    obj: any,
    serviceItemList: ServiceItemType[],
    purposeOfTravelItemList: PurposeOfTravelItemType[],
    causes: Map<string, CauseItemType[]>,
    customerProfiles: IApplicationCustomerProfile[]
  ): string {
    let val = '';
    const type = obj['type'];
    switch (type) {
      case 'date':
      case 'localdate':
        val = this.getDateValue(obj['localDate']);
        break;

      case 'datetime':
      case 'localdatetime':
        val = this.getDateTimeValue(obj['localDate']);
        break;

      case PhoneNumberTypeEnum.Fax:
      case PhoneNumberTypeEnum.Fixed:
      case PhoneNumberTypeEnum.Mobile:
        val = this.getPhoneNumberValue(obj['prefix'], obj['number']);
        break;

      case 'CUSTOMER_PROFILE':
        val = this.getCustomerProfile(obj['customerProfileCode'], customerProfiles);
        break;

      case 'PURPOSE_OF_TRAVEL':
        val = this.getPurposeOfTravel(obj['code'], purposeOfTravelItemList);
        break;

      case 'SERVICE_ITEM':
        val = this.getServiceItem(obj['code'], serviceItemList);
        break;

      case 'CAUSE_LIST':
        val = this.getCause(obj['code'], causes.get('level1'));
        if (!val) {
          val = this.getCause(obj['code'], causes.get('level2'));
        }
        if (!val) {
          val = this.getCause(obj['code'], causes.get('level3'));
        }
        break;
    }

    return val;
  }

  getValueFromType(
    val: any,
    type: string,
    serviceItemList: ServiceItemType[],
    purposeOfTravelItemList: PurposeOfTravelItemType[],
    causeItemList: CauseItemType[],
    customerProfiles: IApplicationCustomerProfile[]
  ): string {
    switch (type) {
      case 'date':
      case 'localdate':
        val = this.getDateValue(val);
        break;

      case 'datetime':
      case 'localdatetime':
        val = this.getDateTimeValue(val);
        break;

      case 'amount':
        val = this.getAmountValue(val);
        break;

      case 'boolean':
        val = this.getYesNoValue(val);
        break;

      case 'CUSTOMER_PROFILE':
        val = this.getCustomerProfile(val, customerProfiles);
        break;

      case 'PURPOSE_OF_TRAVEL':
        val = this.getPurposeOfTravel(val, purposeOfTravelItemList);
        break;

      case 'SERVICE_ITEM':
        val = this.getServiceItem(val, serviceItemList);
        break;

      case 'CAUSE_LIST':
        val = this.getCause(val, causeItemList);
        break;
    }

    return val;
  }

  isKnownObjectType(obj: any): boolean {
    const type = obj && obj['type'];
    switch (type) {
      case 'CUSTOMER_PROFILE':
      case 'PURPOSE_OF_TRAVEL':
      case 'SERVICE_ITEM':
      case 'CAUSE_LIST':
      case 'date':
      case 'localdate':
      case 'datetime':
      case 'localdatetime':
      case PhoneNumberTypeEnum.Fax:
      case PhoneNumberTypeEnum.Fixed:
      case PhoneNumberTypeEnum.Mobile:
        return true;
    }

    return false;
  }

  getNestedProperties(obj: any, key: string): any {
    const nested = obj[key];
    if (Array.isArray(nested)) {
      return nested;
    } else {
      return [nested];
    }
  }

  getDateValue(date: string): string {
    if (date) {
      return moment(date).format('DD-MM-YYYY');
    }
    return '';
  }

  getDateTimeValue(dateTime: string): string {
    if (dateTime) {
      return moment(dateTime).format('DD-MM-YYYY HH:MM');
    }
    return '';
  }

  getAmountValue(amount: string): string {
    if (amount !== '') {
      return amount.toString();
      // return this.numberFormatPipe.transform(amount);
    }
    return '';
  }

  getYesNoValue(value: string): string {
    if (value === '') {
      return '';
    }
    return value ? 'Yes' : 'No';
  }

  getPhoneNumberValue(prefix: any, number: any): string {
    if (number) {
      return prefix ? prefix + number : number;
    }
    return '';
  }

  getCustomerProfile(code: string, customerProfiles: IApplicationCustomerProfile[]): string {
    const customerProfileItem = customerProfiles?.find(item => item.profileCode === code);

    return customerProfileItem ? customerProfileItem.profileName : '';
  }

  getPurposeOfTravel(code: string, purposeOfTravelItemList: PurposeOfTravelItemType[]): string {
    const purposeOfTravelItem = purposeOfTravelItemList?.find(item => item.code === code);

    return purposeOfTravelItem ? purposeOfTravelItem.name : '';
  }

  getServiceItem(code: string, serviceItemList: ServiceItemType[]): string {
    const serviceItem = serviceItemList?.find(item => item.code === code);

    return serviceItem ? serviceItem.name : '';
  }

  getCause(code: string, causeItemList: CauseItemType[] | undefined): string {
    const causeItem = causeItemList?.find(item => item.code === code);

    return causeItem ? causeItem.name : '';
  }
}
